import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Settings, 
  Moon, 
  Sun, 
  Bell, 
  HelpCircle, 
  LogOut,
  ChevronRight,
  Flame,
  Zap,
  BookOpen,
  RotateCcw,
  Lock,
  Check,
  Crown,
  BellRing,
  Globe
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import ProgressRing from '../components/common/ProgressRing';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import NotificationSettings from '../components/notifications/NotificationSettings';
import notificationService from '../services/notificationService';
import { COURSES } from '../i18n';

// Module names
const MODULE_NAMES = {
  "daily-life-1": "Daily Life - Básico",
  "daily-life-2": "Daily Life - Intermediário",
  "daily-life-3": "Daily Life - Avançado",
  "work-1": "Work & Factory - Básico",
  "work-2": "Work & Factory - Intermediário",
  "truck-1": "Truck & Logistics - Básico",
  "truck-2": "Truck & Logistics - Avançado",
  "business-1": "Business - Básico",
  "business-2": "Business - Avançado",
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userProgress, resetProgress } = useApp();
  const { user, logout } = useAuth();
  const [darkMode, setDarkMode] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Get current course info
  const currentCourse = COURSES[user?.selected_course || 'pt-en'];

  useEffect(() => {
    // Initialize notification service and check status
    const settings = notificationService.loadSettings();
    setNotificationsEnabled(settings?.enabled || false);
    notificationService.initialize();
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const totalXP = user?.total_xp || userProgress.totalXP;
  const level = user?.level || userProgress.level;
  const levelProgress = (totalXP % 500) / 500 * 100;
  const xpToNextLevel = 500 - (totalXP % 500);

  return (
    <Layout title="Perfil" showBack>
      <div className="px-4 py-6 space-y-6">
        {/* Profile Header */}
        <div className="card-elevated p-6 text-center space-y-4">
          <div className="relative inline-block">
            <ProgressRing progress={levelProgress} size={100} strokeWidth={6}>
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-10 h-10 text-primary" />
                </div>
              )}
            </ProgressRing>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
              {level}
            </div>
          </div>
          
          <div>
            <h2 className="font-display text-xl font-bold flex items-center justify-center gap-2">
              {user?.name || 'Estudante'}
              {user?.is_admin && <Crown className="w-5 h-5 text-warning" />}
            </h2>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {xpToNextLevel} XP para o nível {level + 1}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-accent mb-1">
                <Flame className="w-4 h-4" />
                <span className="font-bold">{user?.current_streak || userProgress.currentStreak}</span>
              </div>
              <p className="text-xs text-muted-foreground">Dias Seguidos</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-primary mb-1">
                <Zap className="w-4 h-4" />
                <span className="font-bold">{totalXP}</span>
              </div>
              <p className="text-xs text-muted-foreground">XP Total</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-success mb-1">
                <BookOpen className="w-4 h-4" />
                <span className="font-bold">{user?.lessons_completed?.length || userProgress.lessonsCompleted}</span>
              </div>
              <p className="text-xs text-muted-foreground">Lições</p>
            </div>
          </div>
        </div>

        {/* Current Course */}
        <div className="card-elevated p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            {t('courses.currentCourse')}
          </h3>
          <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{currentCourse?.flag}</span>
              <div>
                <p className="font-medium">{currentCourse?.nameNative}</p>
                <p className="text-sm text-muted-foreground">{currentCourse?.descriptionNative}</p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full mt-3"
            onClick={() => navigate('/select-course')}
          >
            <Globe className="w-4 h-4 mr-2" />
            {t('courses.changeCourse')}
          </Button>
        </div>

        {/* Unlocked Modules */}
        <div className="card-elevated p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Meus Módulos
          </h3>
          <div className="space-y-2">
            {Object.entries(MODULE_NAMES).map(([moduleId, moduleName]) => {
              const isUnlocked = user?.unlocked_modules?.includes(moduleId);
              return (
                <div
                  key={moduleId}
                  className={cn(
                    'flex items-center justify-between p-3 rounded-lg',
                    isUnlocked ? 'bg-success/10' : 'bg-muted'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {isUnlocked ? (
                      <Check className="w-4 h-4 text-success" />
                    ) : (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    )}
                    <span className={cn(
                      'text-sm',
                      isUnlocked ? 'text-foreground' : 'text-muted-foreground'
                    )}>
                      {moduleName}
                    </span>
                  </div>
                  {isUnlocked && (
                    <span className="text-xs text-success font-medium">Liberado</span>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Entre em contato para desbloquear mais módulos
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/achievements')}
            className="card-elevated p-4 text-left flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center">
              <span className="text-lg">🏆</span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">{userProgress.badges.filter(b => b.earned).length} Badges</p>
              <p className="text-xs text-muted-foreground">Conquistados</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>

          <button
            onClick={() => navigate('/review')}
            className="card-elevated p-4 text-left flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <RotateCcw className="w-5 h-5 text-accent" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">{userProgress.reviewQueue} Frases</p>
              <p className="text-xs text-muted-foreground">Para revisar</p>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Settings */}
        <div className="space-y-4">
          <h3 className="font-display font-semibold text-lg flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configurações
          </h3>

          <div className="card-elevated divide-y divide-border">
            {/* Dark Mode */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {darkMode ? (
                  <Moon className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Sun className="w-5 h-5 text-muted-foreground" />
                )}
                <div>
                  <p className="font-medium text-sm">Modo Escuro</p>
                  <p className="text-xs text-muted-foreground">
                    {darkMode ? 'Ativado' : 'Desativado'}
                  </p>
                </div>
              </div>
              <Switch
                checked={darkMode}
                onCheckedChange={toggleDarkMode}
              />
            </div>

            {/* Notifications */}
            <button 
              onClick={() => setShowNotificationSettings(true)}
              className="flex items-center justify-between p-4 w-full"
            >
              <div className="flex items-center gap-3">
                <BellRing className="w-5 h-5 text-muted-foreground" />
                <div className="text-left">
                  <p className="font-medium text-sm">Lembretes de Estudo</p>
                  <p className="text-xs text-muted-foreground">
                    {notificationsEnabled ? 'Ativados' : 'Configurar notificações'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {notificationsEnabled && (
                  <span className="text-xs text-success font-medium bg-success/10 px-2 py-1 rounded-full">
                    ON
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </button>

            {/* Help */}
            <button className="flex items-center justify-between p-4 w-full">
              <div className="flex items-center gap-3">
                <HelpCircle className="w-5 h-5 text-muted-foreground" />
                <div className="text-left">
                  <p className="font-medium text-sm">Ajuda</p>
                  <p className="text-xs text-muted-foreground">
                    FAQ e suporte
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Admin Button */}
        {user?.is_admin && (
          <Button
            onClick={() => navigate('/admin')}
            className="w-full"
            variant="outline"
          >
            <Crown className="w-4 h-4 mr-2" />
            Painel de Administrador
          </Button>
        )}

        {/* Logout */}
        <Button
          variant="outline"
          className="w-full border-destructive/50 text-destructive hover:bg-destructive/10"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sair da Conta
        </Button>

        {/* App Info */}
        <div className="text-center space-y-1 pt-4">
          <div className="flex items-center justify-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">FO</span>
            </div>
            <span className="font-display font-semibold">Fuse Online BR</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Versão 1.0.0 • Inglês para Brasileiros
          </p>
          <p className="text-xs text-muted-foreground">
            br.fuseschool.online
          </p>
        </div>
      </div>

      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-background rounded-2xl p-6 shadow-xl animate-in fade-in zoom-in-95 duration-200">
            <NotificationSettings 
              onClose={() => {
                setShowNotificationSettings(false);
                // Update notification status
                const settings = notificationService.loadSettings();
                setNotificationsEnabled(settings?.enabled || false);
              }} 
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProfilePage;
